import { HttpStatusCode } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";

import { getTimeDiffString } from "../../../libs/dateFormat";
import { formatCount } from "../../../libs/numberFormat";
import { formatPostTitle } from "../../../libs/talk";
import { safeReplaceAll } from "../../../libs/replace";

import {
  deleteComment,
  postComment,
  TalkListItem,
  getPostComment,
  Pageable,
  PostComment,
  likeRecode,
  unlikePost,
  deletePost,
  getTalkPostLike,
  unlikeComment,
  getTalkDetail,
  TalkDetail,
  putComment,
  PageableRequest,
} from "../../../services/talkServices";
import { useUserContext } from "../../../contexts/userContext";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";
import useInfiniteScroll, {
  INFINITE_CODE,
} from "../../../hooks/useInfiniteScroll";

import StickyHeader from "../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../components/Rule/Horizontal";
import UserProfile from "../../../components/UserProfile";
import CommonButton from "../../../components/Button";
import CommonPopup from "../../../components/Popup";
import CommonTextArea from "../../../components/TextArea";
import HeightExpandDiv from "../../../components/HeightExpandDiv";
import SimplePopup from "../../../components/Popup/Simple";
import PinchZoomImage from "../../../components/PinchZoomImage";
import TextArea from "../../../components/TextArea";
import CommonProfile from "../../../components/MyInfo/Profile";

import { NoDataIcon } from "../../Center";

type Props = {};

// eslint-disable-next-line no-empty-pattern
export default function TalkDetailPage(props: Props) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [detail, setDetail] = useState<Partial<TalkDetail>>({});
  const [comment, setComment] = useState("");
  const [popup, setPopup] = useState("");
  const [commentPageable, setCommentPageable] = useState<Pageable>();
  const [commentList, setCommentList] = useState<PostComment[]>([]);
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = useState(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [image, setImage] = useState<File | string>("");
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "post" | "comment";
    id: number;
  } | null>(null);
  const [popupComponent, { open }] = useSimplePopup({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [replaceContent, setReplaceContent] = useState("");

  const loadDetail = async () => {
    if (!params.id) return;
    try {
      const { status, data } = await getTalkDetail(params.id);
      if (status === HttpStatusCode.Ok) {
        setDetail((prev) => ({ ...prev, ...data }));
        setReplaceContent(replaceLinks(data?.content));
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("게시글을 불러오는데 실패했습니다.");
      }
    }
  };

  const loadLike = async () => {
    if (!params.id) return;
    try {
      getTalkPostLike(params.id).then((res) => {
        if (res.status === HttpStatusCode.Ok) {
          setDetail((prev) => ({ ...prev, postLike: res.data }));
        }
      });
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("좋아요 정보를 불러오는데 실패했습니다.");
      }
    }
  };

  const loadPostComment = async ({
    page = 1,
    pageSize = 10,
  }: PageableRequest) => {
    let returnValue = INFINITE_CODE.ERROR;

    setIsLoading(true);
    try {
      const res = await getPostComment({
        lifePostId: Number(params.id),
        page,
        pageSize,
      });
      if (res.status === HttpStatusCode.Ok) {
        setDetail((prev) => ({
          ...prev,
          commentCount: res.data.pageable.total,
        }));
        setCommentPageable(res.data.pageable);
        if (page === 1) {
          setCommentList(res.data.items);
        } else {
          setCommentList((prev) => [...prev, ...res.data.items]);
        }
      }
      returnValue =
        res.data.items.length +
        res.data.items
          .map((item) => item.commentList?.length ?? 0)
          .reduce((prev, cur) => prev + cur, 0);
    } catch (e: any) {
      setCommentList([]);
    } finally {
      setIsLoading(false);
    }

    return returnValue;
  };

  const getTitle = () => {
    switch (popup) {
      case "postAction":
        return "글 활동";
      default:
        return "";
    }
  };

  const handleInsertComment = async () => {
    if (!comment) {
      open("댓글을 입력해주세요.");
      return;
    }

    if (detail.lifePostId) {
      setIsLoading(true);
      try {
        await postComment({
          lifePostId: detail.lifePostId,
          content: comment,
        });
        setComment("");
        loadPostComment({ page: 1, pageSize: 10 });
      } catch (e: any) {
        if (e?.response?.data?.error) {
          open(e.response.data.error?.replace(". ", ".\n"));
        } else {
          open("댓글 등록에 실패했습니다.");
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      open("게시글 정보를 찾을 수 없습니다.");
    }
  };

  function replaceLinks(inputString?: string) {
    if (!inputString) return "";
    const plainString = new DOMParser().parseFromString(
      inputString,
      "text/html"
    );

    // 정규식을 사용하여 링크를 찾고 치환
    const linkRegex = /(?:https?:\/\/|www\.)\S+/g;
    const replacedString = safeReplaceAll(
      plainString.body.textContent?.replace(linkRegex, (match) => {
        // 링크가 http://, https://, www로 시작하지 않으면 http://를 추가하여 완전한 URL로 만듦
        const url =
          match.startsWith("http://") || match.startsWith("https://")
            ? match
            : "http://" + match;

        // return `<span class="text-blue-400 custom-link">${match}</span>`;
        return `<a href="${url}" target="_blank" class="text-blue-400">${match}</a>`;
      }) ?? "",
      "\n",
      "<br/>"
    );

    return replacedString || "";
  }

  const [, { resetScroll }] = useInfiniteScroll({
    callback: async (args) => {
      return loadPostComment(args);
    },
    isLoading: isLoading,
  });

  useEffect(() => {
    if (user.isLoading) return;
    setIsLoading(true);
    resetScroll();
    Promise.allSettled([
      loadDetail(),
      loadLike(),
      loadPostComment({ page: 1, pageSize: 10 }),
    ]).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, user.isLoading]);

  useEffect(() => {
    const linkElements = containerRef.current?.querySelectorAll(".custom-link");

    linkElements?.forEach((linkElement) => {
      linkElement.addEventListener("click", (e) => {
        e.preventDefault();
        window.open(linkElement?.textContent ?? "", "_blank");
      });
    });

    return () => {
      linkElements?.forEach((linkElement) => {
        linkElement.removeEventListener("click", (e) => {
          e.preventDefault();
          window.open(linkElement?.textContent ?? "", "_blank");
        });
      });
    };
  }, [containerRef?.current, replaceContent]);

  return (
    <div className="">
      {loadingComponent}
      <StickyHeader
        title="샌디톡"
        onBackClick={() => navigate(location?.state?.parentUrl ?? "/talk")}
        className="px-4"
        rightContent={
          detail?.createdBy === user.memberId ? (
            <div
              onClick={() => {
                setPopup("postAction");
              }}
            >
              <ThreeDotIcon />
            </div>
          ) : undefined
        }
      />
      <div className="p-4 flex flex-col gap-4">
        <div>
          <UserProfile
            nickname={detail.nickname ?? ""}
            createdAt={detail.createdAt ?? ""}
            modifiedAt={detail.modifiedAt ?? ""}
            profile={
              detail.memberFileInfo?.items.find(
                (item) => item.scale === "small"
              )?.url
            }
            role={detail.userRole}
          />
        </div>
        <HorizontalRule className="bg-black" margin="0" />

        {image && (
          <PinchZoomImage
            imageList={
              detail.fileInfo?.items
                ?.filter((file) => file.scale === "full")
                .map((file) => file.url) ?? []
            }
            isOpen={isModalOpen}
            onClose={(e: React.MouseEvent) => {
              e.stopPropagation();
              setIsModalOpen(false);
            }}
          />
        )}

        <div className="flex flex-col gap-4">
          {formatPostTitle(detail.title) && (
            <div className="text-xl font-semibold">
              {formatPostTitle(detail.title)}
            </div>
          )}
          <div className="flex flex-col gap-[1px]">
            {detail.fileInfo?.items
              ?.filter((file) => file.scale === "full")
              .map((file, index) => (
                <img
                  key={index}
                  src={file?.url}
                  alt={`${index}`}
                  onClick={() => {
                    setImage(file.url);
                    setIsModalOpen(true);
                  }}
                />
              ))}
          </div>
          <div
            className="text-sm text-brand-neutral-70 break-words"
            dangerouslySetInnerHTML={{
              __html: replaceContent,
            }}
            ref={containerRef}
          />
          <PostFooter
            post={detail as unknown as TalkListItem}
            onClickLike={() => loadLike()}
            type="post"
          />
        </div>
      </div>

      <HorizontalRule thickness={6} margin="0" />

      {detail.useComment ? (
        <div className="flex flex-col p-5 gap-[30px]">
          <div>
            <div className=" text-sm text-brand-neutral-85 h-[37px]">
              댓글 {commentPageable?.total}개
            </div>
            <HorizontalRule className="bg-black" margin="0" />
            <div className="relative pt-2.5">
              <TextArea
                value={comment}
                className="w-full text-base text-brand-neutral-50"
                placeholder="댓글을 입력해주세요."
                maxLength={400}
                onChange={(e) =>
                  setComment((prev) => e.target.value.slice(0, 400))
                }
                inputClassName="p-2"
              />
              <CommonButton
                className="absolute bottom-2 right-2 py-1.5 text-brand-neutral-60"
                variant="tertiary"
                onClick={handleInsertComment}
              >
                등록
              </CommonButton>
            </div>
          </div>

          {commentList.length > 0 ? (
            <>
              {commentList?.map((comment) => (
                <EachComment
                  key={comment.lifeCommentId}
                  comment={comment}
                  onReload={() => loadPostComment({})}
                  postCreator={detail.createdBy}
                  onClickReport={(comment) => {
                    navigate(`/talk/report/comment/${comment?.lifeCommentId}`, {
                      state: { postId: comment.lifePostId },
                    });
                  }}
                  updateComment={(nextComment: PostComment) => {
                    setCommentList((prev) =>
                      prev.map((currentComment) =>
                        nextComment.lifeCommentId ===
                        currentComment.lifeCommentId
                          ? comment
                          : currentComment
                      )
                    );
                  }}
                />
              ))}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-[208px] gap-10">
              <NoCommentIcon />
              <div className="w-full flex items-center justify-center text-brand-neutral-60">
                첫 댓글을 남겨보세요.
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
          <NoDataIcon />
          댓글이 비허용된 게시글입니다.
        </div>
      )}

      <CommonPopup
        isOpen={popup !== ""}
        onClose={() => setPopup("")}
        title={getTitle()}
      >
        {popup === "postAction" && (
          <div className="flex flex-col gap-2">
            <CommonButton
              onClick={() => {
                navigate(`/talk/editPost/${detail.lifePostId}`);
              }}
            >
              수정하기
            </CommonButton>
            <CommonButton
              onClick={async () => {
                setDeleteTarget({ type: "post", id: Number(params.id) });
                setPopup("deleteConfirm");
              }}
            >
              삭제하기
            </CommonButton>
          </div>
        )}
      </CommonPopup>

      <SimplePopup
        isOpen={popup === "deleteSuccess"}
        message="게시글 삭제되었습니다."
        onClose={() => {
          setPopup("");
          navigate("/talk");
        }}
      />

      <SimplePopup
        isOpen={popup === "deleteCommentSuccess"}
        message="댓글이 삭제되었습니다."
        onClose={() => {
          setPopup("");
          loadPostComment({});
        }}
      />

      <SimplePopup
        isOpen={popup === "deleteFail"}
        message="삭제에 실패하였습니다."
        onClose={() => {
          setPopup("");
        }}
      />

      <CommonPopup
        isOpen={popup === "deleteConfirm"}
        padding={0}
        onClose={() => setPopup("")}
      >
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          삭제 하시겠습니까?
        </div>
        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className=" rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              setIsLoading(true);
              try {
                let result;
                if (deleteTarget?.type === "post") {
                  result = await deletePost(deleteTarget.id);
                  if (result.status === HttpStatusCode.Ok) {
                    setPopup("deleteSuccess");
                  } else {
                    setPopup("deleteFail");
                  }
                } else {
                  result = await deleteComment(deleteTarget?.id as number);
                  if (result.status === HttpStatusCode.Ok) {
                    setPopup("deleteCommentSuccess");
                  } else {
                    setPopup("deleteFail");
                  }
                }
              } catch (e) {
                setPopup("deleteFail");
              } finally {
                setIsLoading(false);
              }
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>
      {popupComponent}
    </div>
  );
}

export type PostFooterProps = {
  post: Partial<TalkListItem>;
  onClickLike?: (id: number) => void;
  type?: "post" | "comment";
  profileCount?: number;
  unUseReport?: boolean;
  isFaq?: boolean;
};

export const PostFooter = ({
  post,
  onClickLike,
  type,
  unUseReport = false,
  profileCount = 2,
  isFaq = false,
}: PostFooterProps) => {
  const [popupComponent, { open }] = useSimplePopup({});
  const navigate = useNavigate();
  const [user] = useUserContext();

  return (
    <div className="flex justify-between">
      <div className="flex gap-1 items-center">
        <div
          onClick={async (e) => {
            e.stopPropagation();
            if (post.postLike?.userLiked) {
              if (type === "post") {
                try {
                  await unlikePost({ lifePostId: post.lifePostId });
                } catch (e: any) {
                  if (e?.response?.data?.error) {
                    open(e.response.data.error?.replace(". ", ".\n"));
                  } else {
                    open("좋아요 취소에 실패했습니다.");
                  }
                }
              }
            } else {
              if (type === "post") {
                try {
                  await likeRecode({ lifePostId: post.lifePostId });
                } catch (e: any) {
                  if (e?.response?.data?.error) {
                    open(e.response.data.error?.replace(". ", ".\n"));
                  } else {
                    open("좋아요 등록에 실패했습니다.");
                  }
                }
              }
            }
            onClickLike?.(post.lifePostId as number);
            // TODO: 유저 리스트 변경되는 것도 적용 필요
          }}
        >
          {post.postLike?.userLiked ?  !isFaq && <FillHeartIcon /> : !isFaq && <BlankHeartIcon />}
        </div>
        <div className="flex items-center">
          {post.postLike?.userList.slice(0, profileCount).map((user, index) => (
            <div
              key={index}
              className={clsx([
                "w-[32px] h-[32px] rounded-full border-[1.5px] border-white flex items-center justify-center",
                { "-ml-1.5": index > 0 },
              ])}
            >
              <CommonProfile
                src={
                  user?.fileInfo?.items?.find((item) => item.scale === "small")
                    ?.url
                }
                sizeVariant="like"
              />
            </div>
          ))}
          {post.postLike?.userList &&
            Math.max(
              post.postLike?.userList.length,
              post?.postLike?.likeCount
            ) > profileCount && (
              <div
                className={clsx([
                  "w-[32px] h-[32px] rounded-full bg-black flex items-center justify-center -ml-1.5 text-[11px] leading-[13.13px] text-white font-semibold z-10",
                ])}
              >
                {`${formatCount(
                  Math.max(
                    post.postLike?.userList?.length,
                    post?.postLike?.likeCount
                  ) - profileCount,
                  999,
                  true
                )}`}
              </div>
            )}
        </div>
      </div>
     { !isFaq && <div className="flex items-center gap-1 text-brand-neutral-50 text-xs ml-1 whitespace-nowrap">
        댓글 <strong>{formatCount(post?.commentCount ?? 0, 999)}</strong>
        <CommentViewEllipse />
        조회 <strong>{formatCount(post?.viewCount ?? 0, 999)}</strong>
        {post.createdBy !== user.memberId && !unUseReport && (
          <>
            <CommentViewEllipse />
            <div
              onClick={() => navigate(`/talk/report/post/${post.lifePostId}`)}
            >
              신고하기
            </div>
          </>
        )}
      </div>}

      {popupComponent}
    </div>
  );
};

type EachCommentProps = {
  comment: PostComment;
  unUseReply?: boolean;
  postCreator?: number;
  onReload?: () => void;
  onClickReport?: (comment: PostComment) => void;
  updateComment?: (comment: PostComment) => void;
};

export const EachComment = ({
  comment,
  unUseReply,
  postCreator,
  onReload,
  onClickReport,
  updateComment,
}: EachCommentProps) => {
  const [content, setContent] = useState("");
  const [expand, setExpand] = useState(false);
  const [user] = useUserContext();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [popup, setPopup] = useState("");
  const [deleteCommentId, setDeleteCommentId] = useState<number | null>(null);
  const [popupComponent, { open }] = useSimplePopup({});

  return (
    <div key={comment.lifeCommentId} className="flex flex-col gap-5">
      {loadingComponent}
      <div className="flex justify-between items-center">
        <UserProfile
          nickname={comment.memberNickname}
          isCreator={comment.createdBy === postCreator}
          profile={
            comment.memberProfileImg?.items.length > 0
              ? comment.memberProfileImg?.items.find(
                  (item) => item.scale === "small"
                )?.url
              : undefined
          }
          role={comment?.userRole}
        />
        {!comment.deletedAt && user.memberId === comment.createdBy && (
          <div className="flex gap-[15px]">
            <div
              onClick={() => {
                setContent(comment.content);
                setEditMode(true);
              }}
            >
              <TalkDetailEditIcon />
            </div>
            <div
              onClick={async () => {
                if (comment.lifeCommentId) {
                  setDeleteCommentId(comment.lifeCommentId);
                  setPopup("deleteConfirm");
                }
              }}
            >
              <TalkDetailDeleteIcon />
            </div>
          </div>
        )}
      </div>

      {editMode ? (
        <div className="relative">
          <CommonTextArea
            placeholder="답글을 입력해주세요."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <div className="absolute right-2 bottom-2 flex gap-1">
            <CommonButton
              onClick={() => {
                setEditMode(false);
                setContent("");
              }}
              variant="tertiary"
              className="!py-2"
            >
              취소
            </CommonButton>
            <CommonButton
              onClick={async () => {
                if (comment.lifeCommentId) {
                  setIsLoading(true);
                  try {
                    await putComment(comment.lifeCommentId, {
                      content,
                      status: "PUBLIC",
                    });
                    onReload?.();
                    setContent("");
                    setExpand(false);
                    setEditMode(false);
                  } catch (e: any) {
                    if (e?.response?.data?.error) {
                      open(e.response.data.error?.replace(". ", ".\n"));
                    } else {
                      open("댓글 수정에 실패했습니다.");
                    }
                  } finally {
                    setIsLoading(false);
                  }
                }
              }}
              variant="tertiary"
              className="!py-2"
            >
              수정
            </CommonButton>
          </div>
        </div>
      ) : (
        <>
          <div className="text-base whitespace-pre-line break-all">
            {comment.isHiddenReportedComment
              ? "가려진 댓글입니다."
              : comment.content}
          </div>
          <div className="flex text-xs text-brand-neutral-40 items-center gap-2">
            <div>{getTimeDiffString(comment?.createdAt)}</div>
            {!comment.deletedAt && (
              <>
                <CommentViewEllipse />
                <div
                  onClick={async () => {
                    try {
                      if (comment?.userLiked === true) {
                        await unlikeComment({
                          lifeCommentId: comment.lifeCommentId,
                        });
                      } else {
                        await likeRecode({
                          lifeCommentId: comment.lifeCommentId,
                        });
                      }
                    } finally {
                      updateComment?.({
                        ...comment,
                        userLiked: !comment?.userLiked,
                        likeCount:
                          (comment?.likeCount ?? 0) +
                          (comment?.userLiked ? -1 : 1),
                      });
                      onReload?.();
                    }
                  }}
                >
                  좋아요 {comment?.likeCount ?? 0}
                </div>
              </>
            )}
            {!unUseReply && !comment.deletedAt && (
              <>
                <CommentViewEllipse />
                <div onClick={() => setExpand(true)}>답글쓰기</div>
              </>
            )}
            {!comment.deletedAt && comment.createdBy !== user.memberId && (
              <>
                <CommentViewEllipse />
                <div onClick={() => onClickReport?.(comment)}>신고하기</div>
              </>
            )}
          </div>
        </>
      )}

      {!unUseReply && (
        <HeightExpandDiv expand={expand}>
          <div className="bg-brand-main-page-background flex flex-col p-4 gap-3 rounded">
            <UserProfile
              nickname={user?.nickname}
              profile={user?.profileImage?.filePath}
              role={comment?.userRole ? user?.role : undefined}
            />
            <div className="relative">
              <CommonTextArea
                placeholder="답글을 입력해주세요."
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
              <div className="absolute right-2 bottom-2 flex gap-1">
                <CommonButton
                  onClick={() => {
                    setContent("");
                    setExpand(false);
                  }}
                  variant="tertiary"
                  className="!py-2"
                >
                  취소
                </CommonButton>
                <CommonButton
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      await postComment({
                        lifePostId: comment.lifePostId,
                        content,
                        parentCommentId: comment.lifeCommentId,
                      });
                      onReload?.();
                      setContent("");
                      setExpand(false);
                    } catch (e: any) {
                      if (e?.response?.data?.error) {
                        open(e.response.data.error?.replace(". ", ".\n"));
                      } else {
                        open("답글 등록에 실패했습니다.");
                      }
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  variant="tertiary"
                  className="!py-2"
                >
                  등록
                </CommonButton>
              </div>
            </div>
          </div>
        </HeightExpandDiv>
      )}
      {comment.commentList && comment.commentList.length > 0 && (
        <div className="flex flex-col gap-1">
          {comment.commentList?.map((comment, index) => (
            <div
              key={index}
              className="bg-brand-main-page-background flex flex-col p-4 gap-2 rounded-lg"
            >
              <EachComment
                comment={comment}
                unUseReply
                postCreator={postCreator}
                onReload={onReload}
                updateComment={(nextComment: PostComment) => {
                  updateComment?.({
                    ...comment,
                    commentList: comment.commentList?.map((currentComment) =>
                      currentComment.lifeCommentId === nextComment.lifeCommentId
                        ? nextComment
                        : currentComment
                    ),
                  });
                }}
                onClickReport={onClickReport}
              />
            </div>
          ))}
        </div>
      )}

      <CommonPopup
        isOpen={popup === "deleteConfirm"}
        padding={0}
        onClose={() => setPopup("")}
      >
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          삭제 하시겠습니까?
        </div>
        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className=" rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              setIsLoading(true);
              try {
                const result = await deleteComment(deleteCommentId as number);

                if (result.status === HttpStatusCode.Ok) {
                  setPopup("deleteCommentSuccess");
                  onReload?.();
                } else {
                  setPopup("deleteFail");
                }
              } catch (e) {
                setPopup("deleteFail");
              } finally {
                setIsLoading(false);
              }
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      <SimplePopup
        isOpen={popup === "deleteCommentSuccess"}
        message="댓글이 삭제되었습니다."
        onClose={() => {
          setPopup("");
        }}
      />

      <SimplePopup
        isOpen={popup === "deleteFail"}
        message="삭제에 실패하였습니다."
        onClose={() => {
          setPopup("");
        }}
      />
      {popupComponent}
    </div>
  );
};

export const CommentViewEllipse = () => (
  <svg
    width="3"
    height="4"
    viewBox="0 0 3 4"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="1.5" cy="2" r="1.5" fill="#D9D9D9" />
  </svg>
);

export const TalkDetailDeleteIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21 5.93359H3"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M14 4.93359H10"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M5 8.93359V21.9336H19C19 20.7518 19 8.93359 19 8.93359"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M14 8.93359V18.4336"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M10 8.93359V18.4336"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const TalkDetailEditIcon = () => (
  <svg
    width="25"
    height="26"
    viewBox="0 0 25 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 14.8225V21.9336H4V5.93359H14"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.4608 4.97281C18.2418 4.19176 19.5082 4.19176 20.2892 4.97281L20.9608 5.64438C21.7418 6.42543 21.7418 7.69176 20.9608 8.47281L11.5858 17.8478C11.2107 18.2229 10.702 18.4336 10.1716 18.4336L7.5 18.4336L7.5 15.762C7.5 15.2316 7.71071 14.7229 8.08579 14.3478L17.4608 4.97281Z"
      stroke="#9F9F9F"
      strokeWidth="1.5"
    />
    <path d="M16.25 6.18359L19.75 9.68359" stroke="#9F9F9F" strokeWidth="1.5" />
  </svg>
);

export const ThreeDotIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4.5 12H7.5" stroke="#1D1929" strokeWidth="3" />
    <path d="M10.5 12H13.5" stroke="#1D1929" strokeWidth="3" />
    <path d="M16.5 12H19.5" stroke="#1D1929" strokeWidth="3" />
  </svg>
);

export const NoCommentIcon = () => (
  <svg
    width="60"
    height="61"
    viewBox="0 0 60 61"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.3">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M45.4083 46.6813C40.7901 51.3 34.7112 53.5366 28.6702 53.406C20.4665 53.2286 6.07812 53.3574 6.07812 53.3574C6.07812 53.3574 6.20429 38.8004 6.1981 30.4542C6.19373 24.5725 8.43314 18.6921 12.9284 14.1978C21.8892 5.23376 36.4476 5.23376 45.4083 14.1955C54.3853 23.1734 54.3691 37.7195 45.4083 46.6813Z"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M19.4767 31.4388H19.2187"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M29.2345 31.4388H28.9765"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M38.9943 31.4388H38.7363"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M42.6981 24.3375C48.6086 24.3375 53.4 19.5461 53.4 13.6356C53.4 7.72502 48.6086 2.93359 42.6981 2.93359C36.7875 2.93359 31.9961 7.72502 31.9961 13.6356C31.9961 19.5461 36.7875 24.3375 42.6981 24.3375Z"
        fill="black"
        stroke="white"
        strokeWidth="4"
      />
      <path
        d="M45.9508 10.3828L39.4453 16.8883"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M39.4453 10.3828L45.9508 16.8883"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const FillHeartIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="16" fill="#F7F7F7" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.9938 9.91615C14.1944 7.81913 11.1938 7.25504 8.93923 9.17528C6.68468 11.0955 6.36727 14.3061 8.13778 16.5771C9.60984 18.4654 14.0648 22.4478 15.5249 23.7368C15.6882 23.881 15.7699 23.9531 15.8652 23.9815C15.9483 24.0062 16.0393 24.0062 16.1225 23.9815C16.2178 23.9531 16.2994 23.881 16.4628 23.7368C17.9229 22.4478 22.3778 18.4654 23.8499 16.5771C25.6204 14.3061 25.3417 11.0753 23.0484 9.17528C20.7551 7.27524 17.7933 7.81913 15.9938 9.91615Z"
      stroke="url(#paint0_linear_1322_27574)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="url(#paint0_linear_1322_27574)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1322_27574"
        x1="25"
        y1="8"
        x2="6.1472"
        y2="13.4587"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const BlankHeartIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="16" fill="#F7F7F7" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.9938 9.91615C14.1944 7.81913 11.1938 7.25504 8.93923 9.17528C6.68468 11.0955 6.36727 14.3061 8.13778 16.5771C9.60984 18.4654 14.0648 22.4478 15.5249 23.7368C15.6882 23.881 15.7699 23.9531 15.8652 23.9815C15.9483 24.0062 16.0393 24.0062 16.1225 23.9815C16.2178 23.9531 16.2994 23.881 16.4628 23.7368C17.9229 22.4478 22.3778 18.4654 23.8499 16.5771C25.6204 14.3061 25.3417 11.0753 23.0484 9.17528C20.7551 7.27524 17.7933 7.81913 15.9938 9.91615Z"
      stroke="url(#paint0_linear_1322_27574)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1322_27574"
        x1="25"
        y1="8"
        x2="6.1472"
        y2="13.4587"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);
